import { useEffect, useState } from "react";
import { PropertyCardProps } from "../components/property-card/property-card.tsx";
import { usePropertyDetailsContext } from "./contexts/property-details-context.tsx";

const COOKIE_NAME = "recentlyViewedProperties";
const MAX_COOKIE_IDS = 10;
const COOKIE_MAX_AGE = "2592000"; // 30 days in seconds

function getRecentlyViewedIds(): string[] {
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${COOKIE_NAME}=`));
  if (!match) return [];
  try {
    return JSON.parse(decodeURIComponent(match.split("=").slice(1).join("="))) as string[];
  } catch {
    return [];
  }
}

function setRecentlyViewedIds(ids: string[]): void {
  const encoded = encodeURIComponent(JSON.stringify(ids));
  document.cookie = `${COOKIE_NAME}=${encoded}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function addToRecentlyViewed(id: string): string[] {
  const existing = getRecentlyViewedIds().filter((i) => i !== id);
  const updated = [id, ...existing].slice(0, MAX_COOKIE_IDS);
  setRecentlyViewedIds(updated);
  return updated;
}

export function useRecentlyViewedProperties(): PropertyCardProps[] {
  const { propertyId, propertySource } = usePropertyDetailsContext();
  const [properties, setProperties] = useState<PropertyCardProps[]>([]);

  useEffect(() => {
    if (!propertyId) return;

    // Store the root property id: the bulk lookup keys on the property id, so keeping the
    // "{propertyId}_{source}" form lets non-espc properties resolve.
    const currentId = propertySource ? `${String(propertyId)}_${propertySource}` : String(propertyId);
    const allIds = addToRecentlyViewed(currentId);
    const otherIds = allIds.filter((id) => id !== currentId).slice(0, 3);

    if (otherIds.length === 0) return;

    const params = new URLSearchParams();
    otherIds.forEach((id) => params.append("ids", id));

    fetch(`/property/recently-viewed?${params.toString()}`)
      .then((res) => res.json())
      .then((data: PropertyCardProps[]) => setProperties(data))
      .catch((err: unknown) => console.error("Failed to load recently viewed properties:", err));
  }, []);

  return properties;
}
